import { Injectable } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { AccountInfo } from "@azure/msal-browser";
import * as t from "io-ts";
import { firstValueFrom, ReplaySubject } from "rxjs";
import { switchMap } from "rxjs/operators";
import { ManagedReceivingGlobalApiV2 as ApiV2 } from "@capstone/mock-api";
import { UserInfoDto } from "mock-api/src/managed-receiving-global/types-v2";
import {
  decodeOrElseNull,
  getEnumMember,
  throwUnhandledCaseError,
  UUID,
} from "src/utils";
import { UserRole } from "../b2c-users.service";
import { mapClaimsToPermissions } from "./map-claims-to-permissions";
import { Permission } from "./permissions";

@Injectable({ providedIn: "root" })
export class UserService implements DetailsProperty<UserDetails> {
  public constructor(private readonly msal: MsalService) {}

  public get details(): UserDetails {
    // The root component and route guards ensures that the user is loaded and
    // valid, so this is safe to assert as non-null for all usages after that.

    return this.#details!;
  }
  #details: UserDetails | null = null;

  public get organizationDetails(): OrganizationDetails {
    // The root component and route guards ensures that the user is loaded and
    // valid, so this is safe to assert as non-null for all usages after that.

    return this.#organizationDetails!;
  }
  #organizationDetails: OrganizationDetails | null = null;

  public get registrationDetails(): UserRegistrationDetails {
    // The root component and route guards ensures that the user is loaded and
    // valid, so this is safe to assert as non-null for all usages after that.

    return this.#registrationDetails!;
  }
  #registrationDetails: UserRegistrationDetails | null = null;

  readonly #isLoadedNotifier = new ReplaySubject<true>(1);
  readonly #isRegistrationDetailsLoadedNotifier = new ReplaySubject<true>(1);
  public readonly isLoadedChanges = this.#isLoadedNotifier.asObservable();
  public readonly isLoaded = firstValueFrom(
    this.#isLoadedNotifier.pipe(
      // Wait for the user registration details to be loaded before completing.
      switchMap(() => this.#isRegistrationDetailsLoadedNotifier),
    ),
  );

  public get isRegistered(): boolean {
    return this.registrationDetails?.isRegistered ?? false;
  }

  public get isValid(): boolean {
    return (this.#details?.isRegistered && this.details?.hasPolicy) ?? false;
  }

  public get permissions(): ReadonlySet<Permission> {
    return this.#details?.permissions ?? new Set();
  }

  public load(
    account: AccountInfo,
    userInfo: UserInfoDto,
    role: UserRole | null,
  ): void {
    this.#details = getDetails(userInfo, role);
    this.#organizationDetails = getUserOrganizationDetails(account);
    this.#isLoadedNotifier.next(true);
  }

  public loadRegistrationDetails(details: ApiV2.UserInfoDto): void {
    this.#registrationDetails = details;
    this.#isRegistrationDetailsLoadedNotifier.next(true);
  }

  public updatePermissions(role: UserRole): void {
    if (this.#details) {
      this.#details = {
        ...this.#details,
        permissions: mapClaimsToPermissions(role.toString()),
      };
    }
  }

  public isSame(user: { readonly userId: UUID }): boolean {
    return this.#details?.id.isSame(user.userId) ?? false;
  }

  public isCarrier(
    this: this,
  ): this is this & DetailsProperty<{ readonly type: UserType.Carrier }> {
    return this.#details?.type === UserType.Carrier;
  }
  public isPartner(
    this: this,
  ): this is this & DetailsProperty<{ readonly type: UserType.Partner }> {
    return this.#details?.type === UserType.Partner;
  }
  public isVendor(): boolean {
    return this.#details?.type === UserType.Vendor;
  }

  public logout(): void {
    this.msal.logout();
  }
}

function getDetails(
  userInfo: UserInfoDto,
  role: UserRole | null,
): UserDetails | null {
  return {
    carrierKey: userInfo.mcDotNumber,
    displayName: userInfo.displayName,
    emailAddress: userInfo.email,
    id: new UUID(userInfo.b2cId),
    permissions: role ? mapClaimsToPermissions(role.toString()) : new Set(),
    type: role ? getUserTypeDetails(role) : null,
    isRegistered: userInfo.isRegistered,
    hasPolicy: userInfo.hasPolicy,
  };
}

function getUserTypeDetails(userRole: UserRole): UserType {
  switch (userRole) {
    case UserRole.Carrier:
      return UserType.Carrier;
    case UserRole.GuardPlus:
    case UserRole.Guard:
    case UserRole.HelpdeskPlus:
    case UserRole.Helpdesk:
    case UserRole.LogisticsWorker:
    case UserRole.OpsLeaderPlus:
    case UserRole.OpsLeader:
    case UserRole.OpsWorker:
    case UserRole.PartnerAdmin:
    case UserRole.Executive:
    case UserRole.Buyer:
      return UserType.Partner;
    case UserRole.Vendor:
      return UserType.Vendor;
    case UserRole.Admin:
    case UserRole.Unknown:
      return UserType.Unknown;
    default:
      throwUnhandledCaseError("user role", userRole);
  }
}

export enum UserType {
  Unknown,
  Partner,
  Carrier,
  Vendor,
}

interface DetailsProperty<T> {
  readonly details: T;
}

export interface UserDetails {
  readonly displayName: string;
  readonly emailAddress: string;
  readonly id: UUID;
  type: UserType | null;
  permissions: ReadonlySet<Permission>;
  carrierKey: string | null;
  hasPolicy: boolean;
  isRegistered: boolean;
}

function getUserOrganizationDetails(
  account: AccountInfo,
): OrganizationDetails | null {
  const idToken = decodeRegistrationToken(account.idTokenClaims);
  if (!idToken) {
    return null;
  }

  return {
    mcOrDotNumber: idToken.extension_Signup_MC_DOT_Number || null,
    organizationName: idToken.extension_Signup_ExternalOrgName || null,
    organizationType: getEnumMember(
      OrganizationTypeName,
      idToken.extension_ExternalOrgType,
      null,
    ),
  };
}

export enum OrganizationTypeName {
  Carrier = "Carrier",
  Partner = "Partner",
  Vendor = "Vendor",
}

interface OrganizationDetails {
  readonly mcOrDotNumber: string | null;
  readonly organizationName: string | null;
  readonly organizationType: OrganizationTypeName | null;
}

const registrationTokenClaimsCodec = t.intersection([
  t.partial({
    extension_Signup_ExternalOrgName: t.string,
    extension_Signup_MC_DOT_Number: t.string,
  }),
  t.union([
    t.type({
      extension_ExternalOrgType: t.literal(OrganizationTypeName.Carrier),
    }),
    t.type({
      extension_ExternalOrgType: t.literal(OrganizationTypeName.Partner),
    }),
    t.type({
      extension_ExternalOrgType: t.literal(OrganizationTypeName.Vendor),
    }),
    t.partial({
      extension_ExternalOrgType: t.literal(""),
    }),
  ]),
]);
const decodeRegistrationToken = decodeOrElseNull(registrationTokenClaimsCodec);

export interface UserRegistrationDetails {
  readonly hasPolicy: boolean;
  readonly isRegistered: boolean;
}
